import { useEffect, useState, useCallback } from 'react';
import useFetchWithMsal from '../../../useFetchWithMsal';
import { thor_api_urls } from '../../common/Constants';
import { buildLoadingState, buildDataState, buildErrorState } from '../../../utils/StateUtils';
import { useThor } from '../context/ThorContext';
import StateHandler from '../../common/elements/StateHandler';
import Button from '../../common/elements/Button';
import SmallLoadingSpinner from '../../common/elements/SmallLoadingSpinner';
import { serverErrMsg } from '../utils/validators';

const PAGE_SIZE = 25;

export default function ResurrectorManager() {
    const { execute } = useFetchWithMsal();
    const { addToast } = useThor();

    const [experimentsState, setExperimentsState] = useState(buildLoadingState([]));
    const [searchTerm, setSearchTerm] = useState('');
    const [resurrecting, setResurrecting] = useState(new Set());
    const [page, setPage] = useState(0);

    const fetchArchived = useCallback(() => {
        execute('GET', thor_api_urls.listArchivedExperiments)
            .then(data => setExperimentsState(buildDataState(data?.experiments || [])))
            .catch(err => setExperimentsState(buildErrorState(err, [])));
    }, [execute]);

    useEffect(() => { fetchArchived(); }, [fetchArchived]);

    const handleResurrect = (key) => {
        setResurrecting(prev => new Set(prev).add(key));
        execute('PUT', thor_api_urls.resurrectExperiment(key))
            .then(() => {
                addToast(`Experiment ${key} resurrected`, 'success');
                setExperimentsState(prev => buildDataState(
                    (prev.data || []).filter(e => e.key !== key)
                ));
            })
            .catch(err => addToast(serverErrMsg(err, `Failed to resurrect ${key}`), 'danger'))
            .finally(() => setResurrecting(prev => { const next = new Set(prev); next.delete(key); return next; }));
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setPage(0);
    };

    const experiments = experimentsState.data || [];
    const filtered = searchTerm.trim()
        ? experiments.filter(e =>
            e.key.toLowerCase().includes(searchTerm.trim().toLowerCase()) ||
            e.name?.toLowerCase().includes(searchTerm.trim().toLowerCase())
          )
        : experiments;

    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const paginated = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

    return (
        <>
            <div className="field mb-4" style={{ maxWidth: 400 }}>
                <label className="label">Experiment Key</label>
                <div className="control">
                    <input
                        className="input"
                        type="text"
                        placeholder="Search by key or name..."
                        value={searchTerm}
                        onChange={handleSearch}
                    />
                </div>
            </div>

            <StateHandler showLoader={experimentsState.loading} showError={experimentsState.error}>
                <div className="level mb-2">
                    <div className="level-left">
                        <p className="is-size-7 has-text-grey">
                            {experiments.length.toLocaleString()} archived experiments
                            {searchTerm ? ` — ${filtered.length} match` : ''}
                        </p>
                    </div>
                    {totalPages > 1 && (
                        <div className="level-right">
                            <div className="buttons has-addons">
                                <button
                                    className="button is-small"
                                    disabled={page === 0}
                                    onClick={() => setPage(p => p - 1)}
                                >← Prev</button>
                                <button className="button is-small is-static">
                                    {page + 1} / {totalPages}
                                </button>
                                <button
                                    className="button is-small"
                                    disabled={page >= totalPages - 1}
                                    onClick={() => setPage(p => p + 1)}
                                >Next →</button>
                            </div>
                        </div>
                    )}
                </div>

                <table className="table is-fullwidth is-bordered is-hoverable">
                    <thead>
                        <tr>
                            <th>Experiment Key</th>
                            <th>Experiment Name</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginated.length === 0 && (
                            <tr><td colSpan={3} className="has-text-grey has-text-centered">No archived experiments found</td></tr>
                        )}
                        {paginated.map(exp => (
                            <tr key={exp.key}>
                                <td><code>{exp.key}</code></td>
                                <td>{exp.name}</td>
                                <td>
                                    <Button
                                        size="small"
                                        color="success"
                                        disabled={resurrecting.has(exp.key)}
                                        onClick={() => handleResurrect(exp.key)}
                                    >
                                        {resurrecting.has(exp.key) ? <SmallLoadingSpinner /> : 'Resurrect!'}
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {totalPages > 1 && (
                    <div className="level mt-2">
                        <div className="level-left" />
                        <div className="level-right">
                            <div className="buttons has-addons">
                                <button
                                    className="button is-small"
                                    disabled={page === 0}
                                    onClick={() => setPage(p => p - 1)}
                                >← Prev</button>
                                <button className="button is-small is-static">
                                    {page + 1} / {totalPages}
                                </button>
                                <button
                                    className="button is-small"
                                    disabled={page >= totalPages - 1}
                                    onClick={() => setPage(p => p + 1)}
                                >Next →</button>
                            </div>
                        </div>
                    </div>
                )}
            </StateHandler>
        </>
    );
}
